import React, { MouseEventHandler } from 'react';

import SearchInlineRecos from 'components/search/SearchInlineRecos';
import MicrosoftAdsWrapper from 'components/search/MicrosoftAdsWrapper';
import ProductCardQuickShopLink from 'components/common/ProductCardQuickShopLink';
import { evRecommendationClick } from 'events/recommendations';
import useMartyContext from 'hooks/useMartyContext';
import AdsRecosRow from 'components/search/AdsRecosRow';
import { track } from 'apis/amethyst';
import { Product } from 'constants/searchTypes';
import { ProductSearch } from 'components/landing/ProductSearch';

import css from 'styles/components/search/inlineAdSlot.scss';

interface PortalProps {
  WrapperEl?: string;
  className: string;
  rowIndex?: number;
  attributes: { [key: string]: any };
  children?: Element;
}

interface Props {
  [key: string]: any;
}

const InlineAdSlot = ({
  trackMicrosoftAdImpressions,
  makeCardClick,
  msftResults,
  heartsInfo,
  inlineRecos,
  filters: { sort, page, term },
  crossSiteRecos,
  isLoadingSymphony,
  productCardsCount
}: Props) => {
  const { marketplace: { search: { msaMelodyImageParams, hasMicrosoftAds, microsoftAdsRowIndex, searchInlineRecosRowIndex } } } = useMartyContext();

  const hasDefaultSort = !sort || sort?.bestForYou || sort?.relevance || !Object.keys(sort).length;
  const hasMarketplaceRecos = crossSiteRecos?.products?.length > 3 && !page && hasDefaultSort;
  const hasInlineRecos = inlineRecos?.recos?.length >= 2;
  const numMicrosoftAds = msftResults?.results?.length;
  const msftAdsToShow = !!numMicrosoftAds && hasMicrosoftAds;

  const makeMicrosoftAdClick = (product: Product, index: number) => {
    const onCardClick = makeCardClick(product, index);
    const { productId, styleId, colorId } = product;
    return (e: MouseEventHandler) => {
      track(() => ([
        evRecommendationClick, {
          index,
          recommendationType: 'PRODUCT_RECOMMENDATION',
          productIdentifiers: {
            productId,
            styleId,
            colorId
          },
          recommendationSource: 'MICROSOFT',
          widgetType: 'MICROSOFT_TOP_BLOCK',
          sourcePage: 'SEARCH_PAGE'
        }
      ]));

      onCardClick(e);
    };
  };

  // default is P13N product ads
  let portalProps: PortalProps = { className: '', attributes: {} };
  let portalContent;

  if (isLoadingSymphony) {
    return null;
  }

  if (hasInlineRecos) {
    portalProps = {
      WrapperEl: 'aside',
      className: css.inlineAdWrapper,
      rowIndex: searchInlineRecosRowIndex,
      attributes: { 'data-reco-count': inlineRecos.recos.length, 'data-test-id': 'searchResultsRecos' }
    };

    portalContent = <SearchInlineRecos
      heartsInfo={heartsInfo}
      inlineRecos={inlineRecos}
      msaImageParams={msaMelodyImageParams}
      searchTerm={term}
      CardBottomSlot={ProductCardQuickShopLink}
    />;
  }

  // Microsoft Ads
  if (msftAdsToShow) {
    portalProps = {
      className: css.microsoftAdsWrapper,
      rowIndex: microsoftAdsRowIndex,
      attributes: { 'data-test-id': 'searchResultsAds' }
    };

    portalContent = <MicrosoftAdsWrapper
      numMicrosoftAds={numMicrosoftAds}
      trackMicrosoftAdImpressions={trackMicrosoftAdImpressions}
      msftResults={msftResults}
      msaImageParams={msaMelodyImageParams}
      makeMicrosoftAdClick={makeMicrosoftAdClick}
      msftAdsToShow={msftAdsToShow}
      CardBottomSlot={ProductCardQuickShopLink}
    />;
  }

  // Marketplace Recos
  if (hasMarketplaceRecos) {
    portalProps = {
      className: css.marketplaceRecosWrapper,
      rowIndex: searchInlineRecosRowIndex,
      attributes: { 'data-test-id': 'marketplaceRecos' }
    };

    const slotHeartsData = { onHeartClick: '', hearts: '', heartsList: '' };

    portalContent =
    <ProductSearch
      slotIndex={0}
      slotName={'marketplaceRecos'}
      slotHeartsData={slotHeartsData}
      onComponentClick={() => {}}
      shouldLazyLoad={false}
      isFullWidth={false}
      slotDetails={crossSiteRecos}/>;
  }

  return (
    <AdsRecosRow {...portalProps} cardCount={productCardsCount} debounceWait={100}>
      {portalContent}
    </AdsRecosRow>
  );
};

export default InlineAdSlot;
